import { useLocation } from "react-router-dom";

/**
 * Source identifiers used to tag CompanyProfile / VentureCapitalProfile entry
 * hrefs and navigates via a `?from=...` query param. The destination profile
 * reads the tag (and the accompanying `fromId` / `fromName`) to decide where
 * its back button should land and how to label itself.
 *
 * Two cases this covers:
 *
 * 1. **Cross-profile navigation** (Company → VC, VC → Company). `navigate(-1)`
 *    technically works in-tab (history has the previous profile), but in a
 *    fresh tab it's a no-op, and the generic "Back to Results" label would be
 *    a lie regardless. Tagging lets us route to the specific previous profile
 *    *and* render an honest "Back to {name}" label.
 *
 * 2. **Cross-listing navigation** (Catalyst Events → Company). Same problem
 *    in the new-tab case; the tag lets the fallback land on the right listing.
 */
export const COMPANY_PROFILE_BACK_SOURCE = {
  catalystEvents: "catalyst-events",
  vcProfile: "vc-profile",
} as const;

export type CompanyProfileBackSource =
  (typeof COMPANY_PROFILE_BACK_SOURCE)[keyof typeof COMPANY_PROFILE_BACK_SOURCE];

export const VC_PROFILE_BACK_SOURCE = {
  companyProfile: "company-profile",
} as const;

export type VCProfileBackSource =
  (typeof VC_PROFILE_BACK_SOURCE)[keyof typeof VC_PROFILE_BACK_SOURCE];

/**
 * Shared shape for the `?from=...&fromId=...&fromName=...` triple. `fromId` is
 * the route-segment id of the previous profile (so we can build a direct URL
 * to it); `fromName` is its display name (for the back-button label).
 *
 * `fromId` must be a simple id (no slashes). It's used directly as a URL
 * segment via `/bkg/<entity>/${fromId}`, so a namespaced/slashed id would
 * silently break the route. All current emit sites pass simple ids.
 */
type BackTagOpts<TSource extends string> = {
  from?: TSource;
  fromId?: string;
  fromName?: string;
};

function appendBackTag<TSource extends string>(
  base: string,
  opts?: BackTagOpts<TSource>,
): string {
  if (!opts?.from) return base;
  const params = new URLSearchParams();
  params.set("from", opts.from);
  if (opts.fromId) params.set("fromId", opts.fromId);
  if (opts.fromName) params.set("fromName", opts.fromName);
  return `${base}?${params.toString()}`;
}

/**
 * Build the URL for a BKG CompanyProfile route.
 *
 * Pass `from` (with optional `fromId` / `fromName`) to tag the referring view.
 * The profile uses the tag to pick a back-button destination + label that
 * survives new-tab / deep-link entry. Without a tag, the back button defaults
 * to `/bkg/companies`. `companyId` may be namespaced (`workspace/company/abc`)
 * — only the trailing segment is used.
 */
export function buildCompanyHref(
  workspaceId: string,
  companyId: string,
  opts?: BackTagOpts<CompanyProfileBackSource>,
): string {
  const documentId = companyId.includes("/")
    ? companyId.split("/").pop()
    : companyId;
  return appendBackTag(
    `/workspace/${workspaceId}/bkg/companies/${documentId}`,
    opts,
  );
}

/**
 * Build the URL for a BKG VentureCapitalProfile route. See `buildCompanyHref`
 * for the tagging convention.
 */
export function buildVCHref(
  workspaceId: string,
  vcId: string,
  opts?: BackTagOpts<VCProfileBackSource>,
): string {
  const documentId = vcId.includes("/") ? vcId.split("/").pop() : vcId;
  return appendBackTag(
    `/workspace/${workspaceId}/bkg/venture-capital/${documentId}`,
    opts,
  );
}

/**
 * Parsed back-tag state for a profile view. Single source of truth so the two
 * profile views can't drift on the contract. See `useProfileBackTag`.
 */
export type ProfileBackTag<TSource extends string> = {
  from: TSource | null;
  fromId: string | null;
  fromName: string | null;
  /**
   * True when this profile was reached via a fresh-tab back hop (the previous
   * profile's back button replaced its history entry with this one and set
   * `state.freshTabBack`). Without this flag we'd lose track of the
   * "no-real-history" regime after the location.key changes from "default".
   */
  arrivedViaFreshTabBack: boolean;
  /**
   * True when this view should treat `navigate(-1)` as unsafe — either because
   * this is the first entry of the tab (`location.key === "default"`) or
   * because we're continuing a fresh-tab back chain.
   */
  inFreshTabContext: boolean;
};

/**
 * Read the `?from`/`fromId`/`fromName` query triple and the freshTabBack
 * state flag set by sibling profiles.
 */
export function useProfileBackTag<
  TSource extends string,
>(): ProfileBackTag<TSource> {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const arrivedViaFreshTabBack = !!(
    location.state as { freshTabBack?: boolean } | null
  )?.freshTabBack;
  return {
    from: (params.get("from") as TSource | null) ?? null,
    fromId: params.get("fromId"),
    fromName: params.get("fromName"),
    arrivedViaFreshTabBack,
    inFreshTabContext: location.key === "default" || arrivedViaFreshTabBack,
  };
}
